<template>
    <form ref="form" class="booking-form" novalidate :aria-busy="saving ? 'true' : 'false'" @submit.prevent="save">
        <div class="booking-form-scroll">
            <div v-if="error || Object.keys(errors).length" ref="errorSummary" class="booking-alert booking-error-summary" role="alert" tabindex="-1">
                <strong>{{ error || $t("reservationForm.invalid") }}</strong>
                <ul v-if="Object.keys(errors).length">
                    <li v-for="(messages, field) in errors" :key="field">{{ messages.join(" ") }}</li>
                </ul>
            </div>
            <div v-if="contextError" class="booking-alert" role="alert">
                {{ contextError }} <button type="button" class="booking-text-button" @click="loadContext">{{ $t("reservationForm.retry") }}</button>
            </div>
            <fieldset :disabled="saving" class="booking-form-layout">
                <div class="booking-form-main">
                    <ReservationVisit
                        :preferred-service-id="serviceIdToPreselect"
                        :reservation="reservation"
                        :services="services"
                        :loading="servicesLoading"
                        :error="servicesError"
                        :errors="errors"
                        :restaurants="restaurants"
                        :restaurant-id="selectedRestaurantId"
                        @retry="loadServices"
                        @change-date="changeDate"
                        @select-slot="selectSlot"
                        @change-restaurant="selectRestaurant" />
                    <section v-if="reservation.slot_id" class="booking-form-card" aria-labelledby="booking-placement-title">
                        <div class="booking-section-heading">
                            <span class="booking-section-icon"><feather type="grid" aria-hidden="true" /></span>
                            <div>
                                <h3 id="booking-placement-title">{{ $t("reservationForm.placement") }}</h3>
                                <p>{{ $t("reservationForm.placementHint") }}</p>
                            </div>
                        </div>
                        <p v-if="placementLoading" class="booking-inline-status" role="status">
                            <span class="booking-spinner" />{{ $t("reservationForm.placementPending") }}
                        </p>
                        <div v-else-if="placementError" class="booking-alert" role="alert">
                            {{ placementError }}
                            <button type="button" class="booking-text-button" @click="retryPlacement">{{ $t("reservationForm.retry") }}</button>
                        </div>
                        <TablesAndMenus
                            v-else
                            ref="placement"
                            :key="placementKey"
                            :reservation="reservation"
                            :restaurant_id="selectedRestaurantId"
                            :vueMultiSelect="tableSelection"
                            :formErrors="{ errors }"
                            :tablesIdToPreselect="preselectedTables"
                            @enable-next-button="placementReady = $event"
                            @busy="placementBusy = $event"
                            @blocker="placementBlockerReason = $event"
                            @capacity-status="capacityStatus = $event" />
                    </section>
                    <template v-if="context">
                        <ReservationClient
                            :key="selectedRestaurantId"
                            :client="reservation.client"
                            :restaurant-id="selectedRestaurantId"
                            :fields="context.fields"
                            :required-contact="requiredContact"
                            :errors="errors"
                            :default-country="context.restaurant.default_tel_country || 'FR'"
                            :api="api" />
                        <ReservationOptions
                            :is-waiting="saveMode === 'waiting'"
                            ref="options"
                            :key="`options-${selectedRestaurantId}`"
                            :reservation="reservation"
                            :restaurant="context.restaurant"
                            :restaurant-id="selectedRestaurantId"
                            :widget="context.widget"
                            :employees="context.employees"
                            :files="files"
                            :errors="errors"
                            @update-files="files = $event" />
                    </template>
                    <p v-else-if="contextLoading" class="booking-inline-status" role="status">
                        <span class="booking-spinner" />{{ $t("reservationForm.loadingContext") }}
                    </p>
                </div>
                <aside class="booking-summary" aria-labelledby="booking-summary-title">
                    <span class="booking-summary-eyebrow">{{ context?.restaurant.name }}</span>
                    <h3 id="booking-summary-title">{{ $t("reservationForm.summary") }}</h3>
                    <dl>
                        <div>
                            <dt>{{ $t("reservationForm.date") }}</dt>
                            <dd>{{ reservation.reservation_date.toFormat("ccc dd LLL yyyy") }}</dd>
                        </div>
                        <div>
                            <dt>{{ $t("reservationForm.guests") }}</dt>
                            <dd>
                                {{ $tc("labels.person", totalGuests) }}
                                <small v-if="reservation.nb_children > 0">{{
                                    $tc("labels.booking.reservations.includingXChildren", reservation.nb_children)
                                }}</small>
                            </dd>
                        </div>
                        <div>
                            <dt>{{ $t("reservationForm.schedule") }}</dt>
                            <dd>
                                {{ reservation.slot?.hour_start || "—" }}<small v-if="reservation.service">{{ reservation.service.name }}</small>
                            </dd>
                        </div>
                        <div v-if="clientName">
                            <dt>{{ $t("reservationForm.client") }}</dt>
                            <dd>{{ clientName }}</dd>
                        </div>
                        <div v-if="tableSelection.entitiesSelected.length">
                            <dt>{{ $t("reservationForm.tables.selected") }}</dt>
                            <dd v-for="entity in tableSelection.entitiesSelected" :key="`${entity.type}:${entity.id}`" class="booking-summary-table">
                                {{ $t(`reservationForm.tables.${entity.type === "group" ? "group" : "table"}`) }} {{ tableName(entity) }}
                                <small>{{ entity.roomName }}</small>
                            </dd>
                        </div>
                        <div v-if="extrasTotal > 0">
                            <dt>{{ $t("reservationForm.extras") }}</dt>
                            <dd>{{ formatCurrency(extrasTotal, context?.restaurant.currency || "EUR") }}</dd>
                        </div>
                    </dl>
                    <div
                        v-if="reservation.slot_id"
                        class="booking-capacity-status"
                        :class="{ 'booking-capacity-warning': ['full', 'error'].includes(capacityStatus) }"
                        role="status">
                        <span v-if="capacityStatus === 'checking'" class="booking-spinner" />
                        <feather v-else :type="capacityStatus === 'available' ? 'check-circle' : 'alert-circle'" aria-hidden="true" />
                        <span>{{ capacityMessage }}</span>
                    </div>
                    <p v-else class="booking-help">{{ $t("reservationForm.readyHint") }}</p>
                    <div v-if="showWaitingListButton" class="booking-save-type">
                        <label for="booking-save-type">{{ $t("reservationForm.saveType") }}</label>
                        <select id="booking-save-type" class="form-select" v-model="saveMode">
                            <option value="booking">{{ $t("reservationForm.booking") }}</option>
                            <option value="option">{{ $t("reservationForm.option") }}</option>
                            <option value="waiting">{{ $t("reservationForm.waiting") }}</option>
                        </select>
                    </div>
                </aside>
            </fieldset>
        </div>
        <footer class="booking-form-footer">
            <p v-if="submitHint" id="booking-submit-hint" class="booking-footer-hint" role="status" aria-live="polite">
                <feather type="info" aria-hidden="true" />{{ submitHint }}
            </p>
            <span v-else class="booking-footer-summary"
                >{{ totalGuests }} {{ $tl("labels.personsShort")
                }}<template v-if="reservation.slot"> · {{ reservation.slot.hour_start }}</template></span
            >
            <button type="button" class="booking-button booking-button-secondary" :disabled="saving" @click="$emit('cancel')">
                {{ $tl("labels.form.actions.cancel") }}
            </button>
            <button
                type="submit"
                class="booking-button booking-button-primary"
                :disabled="!canSubmit"
                :aria-describedby="submitHint ? 'booking-submit-hint' : undefined">
                <span v-if="saving" class="booking-spinner" />{{ saving ? $t("reservationForm.saving") : saveLabel }}
            </button>
        </footer>
    </form>
</template>

<script>
import axios from "axios";
import { v4 as uuid } from "uuid";
import ReservationVisit from "./ReservationVisit.vue";
import ReservationClient from "./ReservationClient.vue";
import ReservationOptions from "./ReservationOptions.vue";
import TablesAndMenus from "./steps/TablesAndMenus.vue";
import { createReservationFormApi } from "./reservationFormApi.mjs";
import { tableName } from "./tableSelection.mjs";
import {
    clientDisplayName,
    createReservationDraft,
    createRequestGate,
    guestCount,
    hasValidGuestCounts,
    reservationExtrasTotal,
    reservationPayload,
    submitBlocker,
    waitingPayload,
} from "./reservationFormState.mjs";

export default {
    components: { ReservationVisit, ReservationClient, ReservationOptions, TablesAndMenus },
    props: {
        restaurant_id: { default: null },
        date: { default: null },
        tablesIdToPreselect: { type: Array, default: () => [] },
        serviceIdToPreselect: { default: null },
        slotIdToPreselect: { default: null },
        defaultValues: { type: Object, default: null },
        showWaitingListButton: { type: Boolean, default: true },
    },
    emits: ["saved", "cancel", "busy"],
    data() {
        const reservation = createReservationDraft(
            this.getDateTime(this.date || this.$route.query.date || undefined, false),
            this.userLang,
            this.defaultValues
        );
        return {
            reservation,
            initialVisit: {
                date: reservation.reservation_date.toISODate(),
                adults: reservation.nb_pers,
                children: reservation.nb_children,
            },
            api: createReservationFormApi(axios),
            contextGate: createRequestGate(),
            servicesGate: createRequestGate(),
            selectionGate: createRequestGate(),
            context: null,
            contextLoading: false,
            contextError: null,
            services: [],
            servicesLoading: false,
            servicesError: null,
            placementLoading: false,
            placementError: null,
            placementReady: false,
            placementBusy: false,
            placementBlockerReason: null,
            capacityStatus: "checking",
            tableSelection: { entitiesSelected: [], paxFull: false, isInvalid: false },
            selectedRestaurantId: this.restaurant_id,
            preselectedTables: [...this.tablesIdToPreselect],
            initialSlotId: this.defaultValues?.slot_id || this.slotIdToPreselect,
            files: [],
            errors: {},
            error: null,
            saving: false,
            saveMode: "booking",
            idempotencyKey: uuid(),
        };
    },
    computed: {
        clientName() {
            return clientDisplayName(this.reservation.client);
        },
        totalGuests() {
            return guestCount(this.reservation);
        },
        extrasTotal() {
            return reservationExtrasTotal(this.reservation);
        },
        requiredContact() {
            if (this.saveMode === "waiting") return null;
            const setting = { footprint: "ask_footprint_by", payment: "ask_payment_by" }[this.reservation.option_bank];
            return this.context?.restaurant.payment_notif_settings?.[setting] || null;
        },
        restaurants() {
            return this.$store.getters["restaurants/getRestaurantsByService"]("booking");
        },
        placementKey() {
            return `${this.selectedRestaurantId}:${this.reservation.reservation_date.toISODate()}:${this.reservation.slot_id}`;
        },
        submitBlockerReason() {
            return submitBlocker({
                loading:
                    (!this.context && !this.contextError) ||
                    this.contextLoading ||
                    this.servicesLoading ||
                    this.placementLoading ||
                    this.placementBusy,
                loadFailed: Boolean(this.contextError || this.servicesError || this.placementError),
                hasSlot: Boolean(this.reservation.slot_id && this.reservation.service),
                validGuests: hasValidGuestCounts(this.reservation),
                waiting: this.saveMode === "waiting",
                hasFiles: this.files.length > 0,
                placementReady: this.placementReady,
                placementReason: this.placementBlockerReason,
            });
        },
        canSubmit() {
            return !this.saving && this.submitBlockerReason === null;
        },
        submitHint() {
            if (this.saving || !this.submitBlockerReason) return null;
            return this.$t(`reservationForm.blockers.${this.submitBlockerReason}`);
        },
        saveLabel() {
            return this.$t(
                this.saveMode === "waiting"
                    ? "reservationForm.saveWaiting"
                    : this.saveMode === "option"
                      ? "reservationForm.saveOption"
                      : "reservationForm.save"
            );
        },
        capacityMessage() {
            if (this.capacityStatus === "full") return this.$tl("infos.booking.reservations.saveCauseSurbooking", this.selectedRestaurantId);
            return this.$t(
                this.capacityStatus === "available"
                    ? "reservationForm.capacityAvailable"
                    : this.capacityStatus === "error"
                      ? "reservationForm.requestFailed"
                      : "reservationForm.capacityPending"
            );
        },
        hasChanges() {
            return Boolean(
                this.reservation.slot_id ||
                    this.reservation.reservation_date.toISODate() !== this.initialVisit.date ||
                    this.reservation.nb_pers !== this.initialVisit.adults ||
                    this.reservation.nb_children !== this.initialVisit.children ||
                    this.reservation.client.id ||
                    this.reservation.client.civility ||
                    this.reservation.client.firstname ||
                    this.reservation.client.lastname ||
                    this.reservation.client.company ||
                    this.reservation.client.email ||
                    this.reservation.client.phone.raw ||
                    this.reservation.client.type ||
                    this.reservation.client.is_vip ||
                    this.reservation.client.newsletter ||
                    this.reservation.restaurant_comment ||
                    this.reservation.gift ||
                    this.reservation.room_number ||
                    this.reservation.is_hotel_client ||
                    this.files.length
            );
        },
    },
    methods: {
        tableName,
        async loadContext() {
            const request = this.contextGate.begin();
            this.contextLoading = true;
            this.contextError = null;
            try {
                const context = await this.api.context(this.selectedRestaurantId, request.signal, this.$store.getters["widgets/getWidget"]);
                if (request.current()) this.context = context;
            } catch (error) {
                if (request.current()) this.contextError = this.$t("reservationForm.loadFailed");
            } finally {
                if (request.current()) this.contextLoading = false;
            }
        },
        async loadServices() {
            const request = this.servicesGate.begin();
            this.servicesLoading = true;
            this.servicesError = null;
            try {
                const services = await this.api.services(this.selectedRestaurantId, this.reservation.reservation_date.toISODate(), request.signal);
                if (!request.current()) return;
                this.services = services;
                if (this.initialSlotId) {
                    const service = services.find((service) =>
                        service.slotsWithPax.data.some((slot) => Number(slot.id) === Number(this.initialSlotId))
                    );
                    if (service)
                        await this.selectSlot({
                            service,
                            slot: service.slotsWithPax.data.find((slot) => Number(slot.id) === Number(this.initialSlotId)),
                        });
                    this.initialSlotId = null;
                }
            } catch (error) {
                if (request.current()) this.servicesError = this.$t("reservationForm.loadFailed");
            } finally {
                if (request.current()) this.servicesLoading = false;
            }
        },
        clearSelection(clearExtras = true) {
            this.selectionGate.cancel();
            this.reservation.slot_id = null;
            this.reservation.slot = null;
            this.reservation.service = null;
            this.reservation.ignore_placement = false;
            this.placementReady = false;
            this.placementBusy = false;
            this.placementBlockerReason = null;
            this.placementLoading = false;
            this.placementError = null;
            this.tableSelection = { entitiesSelected: [], paxFull: false, isInvalid: false };
            if (clearExtras) {
                this.reservation.choosen_menus = {};
                this.reservation.choosen_general_options = {};
                delete this.reservation.back_choosen_menus;
                delete this.reservation.back_choosen_general_options;
            }
        },
        changeDate(date) {
            this.clearSelection();
            this.preselectedTables = [];
            this.initialSlotId = null;
            this.reservation.reservation_date = date;
            this.loadServices();
        },
        async selectSlot({ slot, service }) {
            if (this.reservation.slot_id === slot.id && !this.placementError) return;
            const previousServiceId = this.reservation.service?.id;
            this.clearSelection(Boolean(previousServiceId && previousServiceId !== service.id));
            const request = this.selectionGate.begin();
            this.reservation.slot_id = slot.id;
            this.reservation.slot = { id: slot.id, hour_start: slot.hour_start, service_id: service.id };
            this.reservation.service = { ...service };
            this.capacityStatus = "checking";
            if (!this.reservation.is_duration_customized) {
                const duration = service.paxDurations?.data.find((item) => Number(item.pax) === this.totalGuests)?.duration || service.duration;
                const [HH, mm] = duration.split(":");
                this.reservation.duration = { HH, mm };
            }
            this.placementLoading = true;
            try {
                if (service.has_seating_plan) {
                    const details = await this.api.seatingPlan(
                        this.selectedRestaurantId,
                        service.id,
                        this.reservation.reservation_date.toISODate(),
                        request.signal
                    );
                    if (!request.current()) return;
                    this.reservation.service.seatingPlanFilled = details.seatingPlanFilled;
                }
            } catch (error) {
                if (request.current()) this.placementError = this.$t("reservationForm.loadFailed");
            } finally {
                if (request.current()) this.placementLoading = false;
            }
        },
        retryPlacement() {
            this.selectSlot({ slot: this.reservation.slot, service: this.reservation.service });
        },
        selectRestaurant(restaurant) {
            if (!restaurant || Number(restaurant.id) === Number(this.selectedRestaurantId)) return;
            this.clearSelection();
            this.context = null;
            this.errors = {};
            this.reservation.user_id = null;
            this.reservation.room_number = null;
            this.reservation.is_hotel_client = false;
            this.reservation.option_bank = null;
            this.reservation.gift = null;
            this.reservation.waiting_id = null;
            this.preselectedTables = [];
            this.initialSlotId = null;
            this.selectedRestaurantId = restaurant.id;
            this.loadContext();
            this.loadServices();
        },
        async save() {
            if (!this.canSubmit) return;
            this.error = null;
            this.errors = {};
            this.$refs.form.querySelectorAll(":invalid").forEach((element) => {
                let details = element.closest("details");
                while (details) {
                    details.open = true;
                    details = details.parentElement?.closest("details");
                }
            });
            if (!this.$refs.form.reportValidity()) return;
            this.saving = true;
            try {
                if (this.saveMode === "waiting") {
                    await this.api.addToWaitingList(this.selectedRestaurantId, waitingPayload(this.reservation, this.selectedRestaurantId));
                } else {
                    const payload = reservationPayload(
                        this.reservation,
                        this.selectedRestaurantId,
                        this.tableSelection.entitiesSelected,
                        this.idempotencyKey,
                        this.saveMode === "option"
                    );
                    if (!payload.option_bank && !this.$refs.options.canConfirm) payload.send_confirmation = false;
                    await this.api.create(this.selectedRestaurantId, payload, this.files);
                }
                this.$emit("saved");
            } catch (error) {
                this.errors = error.response?.data?.errors || {};
                this.error = this.getErrorMsgFromErrorResponse(error, this.$t("reservationForm.invalid"));
                await this.$nextTick();
                this.$refs.errorSummary?.focus();
            } finally {
                this.saving = false;
            }
        },
    },
    watch: {
        saving(value) {
            this.$emit("busy", value);
        },
    },
    created() {
        this.reservation.slot_id = null;
        this.loadContext();
        this.loadServices();
    },
    beforeUnmount() {
        this.contextGate.cancel();
        this.servicesGate.cancel();
        this.selectionGate.cancel();
    },
};
</script>

<style src="./reservationForm.css"></style>
